'use client'

import { FilterCard } from '@/app/[locale]/collection/_components/filter/filter-carousel/filter-card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCardFacet, TFacet } from '@/types/collection'
import { useShallow } from 'zustand/react/shallow'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'

interface FilterCarouselProp {
  className?: string
  title?: string
  facets: TFacet[]
  card_facets: TCardFacet[]
  hideCarouselTitle?: boolean
  showClearAllCard?: boolean
  clearAllCardTitle?: string
  clearAllCardImage?: string
}

export const FilterCarousel = ({
  className,
  title,
  facets,
  card_facets,
  hideCarouselTitle,
  showClearAllCard,
  clearAllCardTitle,
  clearAllCardImage,
}: FilterCarouselProp) => {
  const { setFilter, filters, setConcatValue, originalSlug, clearFilters } =
    useCollectionFilterStoreV2(
      useShallow((state) => ({
        setFilter: state.setFilter,
        filters: state.filters,
        setConcatValue: state.setConcatValue,
        originalSlug: state.originalSlug,
        clearFilters: state.clearFilters,
      }))
    )

  const cardFacet = card_facets?.[0]

  // Check if attributeCards exists and has items
  const hasAttributeCards =
    cardFacet && cardFacet.facet_values && cardFacet.facet_values.length > 0

  if (!hasAttributeCards) {
    return null
  }

  const attributeCards = cardFacet.facet_values || []

  const filterKey = cardFacet?.seo_alias

  const allFilterCardItems = attributeCards

  // Filter out items that don't have valid images
  const filterCardItems = allFilterCardItems.filter(
    (item: { image: string }) => item.image && item.image.trim() !== ''
  )

  const finalFilterKey = filterKey

  if (!finalFilterKey || filterCardItems.length === 0) {
    return null
  }

  const hasActiveFilters = Object.keys(filters).some(
    (key) => key !== 'page' && filters[key] !== undefined && filters[key] !== ''
  )

  const onChangeFilter = (value: string) => {
    if (value === 'clear-all') {
      clearFilters()
      scrollToProductSection(200, 50)
      return
    }

    // Check if this is a concat filter and update concatValue accordingly
    if (cardFacet?.is_concat_slug) {
      if (value) {
        // Set concatValue when filter is applied
        setConcatValue(value)
      } else {
        // Clear concatValue when filter is removed
        setConcatValue(null)
      }
    }

    setFilter(finalFilterKey, value)

    // Cuộn lên đầu danh sách sản phẩm sau khi thay đổi filter
    scrollToProductSection(200, 50)
  }

  const hasConcatActive = (value: string) => {
    return filters?.[finalFilterKey] === value
  }

  const hasConcatParent = filterCardItems.some((item: { seo_alias: string }) =>
    hasConcatActive(item.seo_alias)
  )

  return (
    <>
      {hideCarouselTitle || !title ? null : (
        <h1 className='mb-2 text-center font-criteria text-lg font-medium uppercase md:mb-4 md:text-2xl'>
          {title}
        </h1>
      )}
      <div className='relative'>
        <Carousel
          opts={{
            align: 'start',
            duration: 50,
            dragFree: true,
            skipSnaps: true,
          }}
          className='w-full'
        >
          <CarouselContent
            className={cn(className, {
              'my-3 md:my-5 lg:justify-center':
                filterCardItems.length + (showClearAllCard ? 1 : 0) < 6,
            })}
          >
            {showClearAllCard && (
              <CarouselItem
                className={cn(
                  'xl:1/6 will-change-transform md:basis-1/4 lg:basis-1/5',
                  cardFacet?.is_vertical
                    ? 'basis-2/5 xl:basis-1/6 2xl:basis-[14.2%]'
                    : 'basis-3/5'
                )}
              >
                <FilterCard
                  title={clearAllCardTitle || 'Tất cả'}
                  imageUrl={clearAllCardImage || ''}
                  filterValue='clear-all'
                  filterKey='clear-all'
                  onChangeFilter={onChangeFilter}
                  isActive={!hasActiveFilters}
                  shouldBeDimmed={hasActiveFilters}
                  isVertical={cardFacet?.is_vertical}
                />
              </CarouselItem>
            )}
            {filterCardItems.map(
              (
                item: {
                  seo_alias: string
                  title: string
                  short_description?: string
                  image: string
                },
                index: number
              ) => (
                <CarouselItem
                  key={index}
                  className={cn(
                    'xl:1/6 will-change-transform md:basis-1/4 lg:basis-1/5',
                    cardFacet?.is_vertical
                      ? 'basis-2/5 xl:basis-1/6 2xl:basis-[14.2%]'
                      : 'basis-3/5'
                  )}
                >
                  <FilterCard
                    title={item.title}
                    description={item.short_description || ''}
                    // TODO: get product count from api
                    // productCount={}
                    imageUrl={item.image}
                    imageHoverUrl={''}
                    filterValue={item.seo_alias}
                    filterKey={finalFilterKey}
                    isUnderWare={false}
                    onChangeFilter={onChangeFilter}
                    hasConcatActive={hasConcatActive(item.seo_alias)}
                    hasConcatParent={hasConcatParent}
                    isOpenNewTab={cardFacet?.is_open_new_tab}
                    isVertical={cardFacet?.is_vertical}
                    originalSlug={originalSlug}
                    facets={facets}
                    cardFacets={card_facets}
                    isConcatSlug={cardFacet?.is_concat_slug}
                    isAllowMultiple={cardFacet?.is_allow_multiple}
                  />
                </CarouselItem>
              )
            )}
          </CarouselContent>
          <CarouselNavigation />
        </Carousel>
      </div>
    </>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-left-10'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-right-10'
        variant='black'
        hideIfEnd
      />
    </>
  )
}
